"use client"

import * as React from "react"
import { ChevronRightIcon } from "lucide-react"

import { cn } from "../../lib/utils"
import { useIsMobile } from "../../lib/use-mobile"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./collapsible"

export type ActionMenuListRenderContext = {
  isMobile: boolean
  close: () => void
}

export type ActionMenuListItem = {
  id: string
  label: React.ReactNode
  /** A short line under the label on phones, saying what the action does when the label alone does not. */
  description?: React.ReactNode
  icon?: React.ReactNode
  disabled?: boolean
  variant?: "default" | "destructive" | "danger"
  onSelect?: () => void
  children?: ActionMenuListItem[]
  render?: (context: ActionMenuListRenderContext) => React.ReactNode
}

type ActionMenuListProps = {
  items: ActionMenuListItem[]
  close?: () => void
  className?: string
  itemClassName?: string
  childrenIndentClassName?: string
}

function ActionMenuList({
  items,
  close = () => undefined,
  className,
  itemClassName,
  childrenIndentClassName,
}: ActionMenuListProps) {
  const isMobile = useIsMobile()
  const neutralInteractiveClassName =
    "hover:bg-muted/70 hover:text-popover-foreground data-[state=open]:bg-muted/70 data-[state=open]:text-popover-foreground dark:hover:bg-white/[0.06] dark:data-[state=open]:bg-white/[0.06]"

  const rowClassName = cn(
    "flex w-full items-center text-left outline-none transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:m-0 [&_svg]:size-4 [&_svg]:shrink-0",
    neutralInteractiveClassName,

    isMobile ? "min-h-11 gap-3 px-4 py-3 text-[15px] leading-5" : "h-8 gap-2 rounded-md px-2 text-sm",
    itemClassName,
  )
  const childRowClassName = cn(rowClassName, childrenIndentClassName ?? (isMobile ? "pl-11" : "pl-8"))

  const renderItems = (menuItems: ActionMenuListItem[], child = false) =>
    menuItems.map((item) => {
      if (item.render) {
        /**
         * A widget row (`render`) supplies its own component — MembersOnboardAction, ExportAction — and those
         * render a real Button, often `emphasis: "primary"`. Untouched, a filled primary button sat between
         * ghost rows: a green block whose label vanished against its own fill.
         *
         * ONLY the surface is neutralised — background, text colour, shadow. The button keeps its own padding,
         * height and internal layout, because overriding those broke the row's structure outright.
         */
        return (
          <div
            key={item.id}
            className={cn(
              "[&>*]:w-full",
              "[&_button]:bg-transparent [&_button]:shadow-none [&_button]:!text-popover-foreground",
              "[&_button:hover]:!bg-muted/70 dark:[&_button:hover]:!bg-white/[0.06]",
            )}
          >
            {item.render({ isMobile, close })}
          </div>
        )
      }

      const itemClassNames = cn(
        child ? childRowClassName : rowClassName,
        (item.variant === "destructive" || item.variant === "danger") && "text-destructive [&_svg]:text-destructive",
      )

      if (item.children && item.children.length > 0) {
        return (
          <Collapsible key={item.id} className='w-full'>
            <CollapsibleTrigger asChild>
              <button type='button' disabled={item.disabled} className={cn("group", itemClassNames)}>
                {item.icon}
                <span className='min-w-0 flex-1 text-left'>{item.label}</span>
                <ChevronRightIcon className='size-4 opacity-60 transition-transform group-data-[state=open]:rotate-90' />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className='overflow-hidden'>
              {renderItems(item.children, true)}
            </CollapsibleContent>
          </Collapsible>
        )
      }

      return (
        <button
          key={item.id}
          type='button'
          disabled={item.disabled}
          onClick={() => {
            item.onSelect?.()
            close()
          }}
          className={itemClassNames}
        >
          {item.icon}
          {isMobile && item.description ? (
            <span className='flex min-w-0 flex-1 flex-col text-left'>
              <span>{item.label}</span>
              <span className='text-muted-foreground text-[13px] leading-snug font-normal'>{item.description}</span>
            </span>
          ) : (
            <span className='min-w-0 flex-1 text-left'>{item.label}</span>
          )}
        </button>
      )
    })

  return (
    <div className={cn("flex w-full flex-col", isMobile ? "divide-y divide-border/60" : "gap-0.5", className)}>
      {renderItems(items)}
    </div>
  )
}

export { ActionMenuList }
